import requester from '@/lib/axios';
import JsonApiResponse from '@/models/JsonApiResponse';
import Lesson from '@/models/Lesson';
import { Ordenable } from '@/models/Model';
import { ScheduledLessonTypeEnum } from '@/models/ScheduledLesson';
import { BaseFilters } from '@/models/Service';
import Unit, { UnitDTO } from '@/models/Unit';
import { LessonDTO } from './lessonServices';
import { jsonToFormData } from '@/utils/shared/jsonToFormData';
import { MULTIPART_FORM_DATA_HEADERS } from '@/constants';

const request = requester();

const basePath = 'lesson-blocks';

export type ListLessonBlocksFilters = BaseFilters & {
  lessonId?: number;
  type?: ScheduledLessonTypeEnum;
};

export async function listLessonBlocks(
  slug: string,
  params: ListLessonBlocksFilters = {},
) {
  const { data } = await request.get<JsonApiResponse<Unit>>(
    `courses/${slug}/${basePath}/`,
    { params },
  );

  return data;
}

export async function getLessonBlock(slug: string, id: number) {
  const { data } = await request.get<Unit>(
    `courses/${slug}/${basePath}/${id}/`,
  );

  return data;
}

export async function deleteLessonBlock(slug: string, id: number) {
  await request.delete(`courses/${slug}/${basePath}/${id}/`);
}

export async function updateLessonBlock(
  slug: string,
  id: number,
  changes: Partial<UnitDTO>,
) {
  const { data } = await request.patch<Unit>(
    `courses/${slug}/${basePath}/${id}/`,
    changes,
  );

  return data;
}

type CreateLessonBlockPayload = Pick<UnitDTO, 'name'> & Partial<Ordenable>;

export async function createLessonBlock(
  slug: string,
  block: CreateLessonBlockPayload,
) {
  const { data } = await request.post<Unit>(
    `courses/${slug}/${basePath}/`,
    block,
  );

  return data;
}

export async function listBlockLessons(
  slug: string,
  id: number,
  params: ListLessonBlocksFilters = {},
) {
  const { data } = await request.get<JsonApiResponse<Lesson>>(
    `courses/${slug}/${basePath}/${id}/lessons/`,
    { params },
  );

  return data;
}

export async function createLessonIntoBlock(
  slug: string,
  blockId: number,
  lesson: LessonDTO,
) {
  const { data } = await request.post<Lesson>(
    `courses/${slug}/${basePath}/${blockId}/lessons/`,
    lesson,
  );

  return data;
}

export async function updateLessonIntoBlock(
  slug: string,
  blockId: number,
  lessonId: number,
  body: Partial<LessonDTO> = {},
) {
  const formData = jsonToFormData(body);
  const { data: updatedLesson } = await request.patch<Lesson>(
    `courses/${slug}/${basePath}/${blockId}/lessons/${lessonId}/`,
    formData,
    { headers: MULTIPART_FORM_DATA_HEADERS },
  );

  return updatedLesson;
}

export async function deleteLessonIntoBlock(
  slug: string,
  blockId: number,
  lessonId: number,
) {
  await request.delete(
    `courses/${slug}/${basePath}/${blockId}/lessons/${lessonId}/`,
  );
}

export type LessonBlockOrder = {
  id: number;
  lessonsIds: Array<{ id: number }>;
};

export async function updateLessonBlocksOrder(
  slug: string,
  order: Array<LessonBlockOrder>,
) {
  await request.patch(`courses/${slug}/${basePath}/order/`, order);
}
